import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { SnackbarPosition } from "../enums/SnackbarPositionEnum";
import { SnackbarType } from "../enums/SnackbarTypeEnum";
import { usePortal } from "./PortalContext";
import Snackbar from "../../components/snackbar/Snackbar";
import "../../components/snackbar/Snackbar.css";

const NotificationContext = createContext(null);

let notificationIdCounter = 0;

const NotificationContainer = ({ notifications, onClose }) => {
  const portalRoot = usePortal();
  const stackRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const container = stackRef.current;
    if (!container) {
      setHasOverflow(false);
      return;
    }
    setHasOverflow(container.scrollHeight > container.clientHeight);
  }, [notifications]);

  if (!portalRoot || notifications.length === 0) return null;

  const stackClasses = [
    "notification-stack",
    hasOverflow && "fade-top",
    hasOverflow && "fade-bottom",
  ].filter(Boolean).join(" ");

  const notificationElements = (
    <div
      className={stackClasses}
      ref={stackRef}
    >
      {notifications.map((notification) => {
        const {
          id,
          type = SnackbarType.INFO,
          message,
          title,
          duration = 4000,
          position = SnackbarPosition.BOTTOM,
        } = notification;

        return (
          <Snackbar
            key={id}
            type={type}
            message={message}
            title={title}
            duration={duration}
            position={position}
            onClose={() => onClose(id)}
            usePortal={false}
          />
        );
      })}
    </div>
  );

  return ReactDOM.createPortal(notificationElements, portalRoot);
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((type, message, options = {}) => {
    const id = ++notificationIdCounter;
    const notification = {
      id,
      type,
      message,
      duration: 4000,
      position: SnackbarPosition.BOTTOM,
      ...options,
    };

    setNotifications((prev) => [...prev, notification]);
    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const updateNotification = useCallback((id, updates) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, ...updates } : n))
    );
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const contextValue = {
    notifications,
    addNotification,
    updateNotification,
    removeNotification,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationContainer
        notifications={notifications}
        onClose={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};
